import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { colors } from "../design-tokens";
import { getMapBrowserKey, getMapStatus } from "./api";
import { loadGoogleMaps, type GoogleNs } from "./mapsLoader";
import {
  BROWSER_GEOCODE_SOURCE, NOT_MAPPED_MESSAGE, MAP_FIT_PADDING_PX, MAP_MAX_ZOOM, METERS_PER_MILE, frameBounds, geocodeMatchPasses,
  mapCanvasStyle, pinStyle, type LatLng, type MapPoint, type UnplacedRow,
} from "./compMapModel";

/**
 * THE COMP-SEARCH MAP (1G): the subject, a circle at the search radius, and one numbered pin per
 * comparable sale — the same numbers the cards carry, so a pin and a card are the same sale.
 *
 *   hover / focus a card   -> its pin grows and the card is the `activeIndex` (the parent owns it)
 *   hover / click a pin    -> `onActiveChange(index)`; a click also asks the parent to bring the
 *                             card into view and focus it (`onPinClick`)
 *   selected vs candidate  -> filled navy disc with a white number vs a white disc with a navy
 *                             ring and number. The number is on every pin and the card says
 *                             "Included", so colour is never the only carrier.
 *
 * KEYBOARD AND SCREEN READER. Every pin is mirrored by a card that is reachable by Tab, and focusing
 * a card lights its pin, so nothing on the map is needed that a keyboard cannot reach. The canvas
 * is a LABELED REGION, not `aria-hidden` (UX review): Google's map puts focusable elements inside
 * it (the map itself, clickable markers), and focusable content under `aria-hidden` is an
 * accessibility failure. A marker activated from the keyboard runs the same click handler, which
 * focuses the matching card.
 *
 * NO KEY, NO BLANK BOX. With the browser key unset (`/api/next/map/status` ->
 * `browser_key_configured: false`) or the map off, this renders the numbered key in the same slot
 * with one calm sentence. The product has no in-app static map image to fall back to (the static
 * map exists only as the server-built deliverable image behind its license gate), so the key list
 * is the fallback and the page still answers "which sale is pin 3?".
 *
 * SQUARE, AND FRAMED ON EVERYTHING (2026-10-22). The canvas is a square (`mapCanvasStyle`, written
 * through `setProperty`, I-8): at most 560 px on a wide screen, the full column at 768 px and below,
 * and beside the search panel at 1024 px and up (the stylesheet's `.vb-cs-body`). The map frames
 * the subject's circle AND every comp pin (`frameBounds`) with padding, on load and again whenever
 * the circle or the set of pins changes (a "Search again"), and never closer than zoom 16. With no
 * comps it frames the circle (0.5 mi at the least), never street level around one pin.
 *
 * THE KEY. The browser reads `VALUBRIEF_MAPS_BROWSER_KEY` through `/api/next/map/browser-key`; it is
 * HTTP-referrer-restricted and distinct from the server key (`VALUBRIEF_MAPS_GCP_KEY`).
 */
export const NO_KEY_SENTENCE =
  "The map isn't turned on yet, so here are the same sales as a numbered list, in the order of the cards below.";
export const LOAD_FAILED_SENTENCE =
  "The map didn't load. The numbered list shows the same sales in the same order, and nothing about your search is affected.";

type Mode = "checking" | "ready" | "nokey" | "failed";

/** The same rows the pins stand for, in words: what each pin is, where its point came from, and the
 * rows that have no location, each with the sentence that says so. */
function PinKey({ points, unplaced, geoPlaced, geoMissed, activeIndex, onActiveChange, mapShown }: {
  mapShown: boolean;
  points: MapPoint[]; unplaced: UnplacedRow[]; geoPlaced: number[]; geoMissed: number[];
  activeIndex: number | null; onActiveChange: (index: number | null) => void;
}) {
  const shown = points.filter((p) => !geoMissed.includes(p.index));
  const missed = points.filter((p) => geoMissed.includes(p.index));
  return (
    <ol className="vb-csm-key" aria-label="Pins and where each location came from" data-testid="map-pin-key">
      <li className="vb-csm-key-subject"><span className="vb-csm-dot vb-csm-dot-home" aria-hidden="true">H</span> Your home</li>
      {shown.map((p) => {
        const src = p.source ?? (geoPlaced.includes(p.index) ? BROWSER_GEOCODE_SOURCE : mapShown ? "locating" : "address only, no map to place it");
        return (
          <li key={p.index} data-active={activeIndex === p.index || undefined} data-kind={p.kind}
              onMouseEnter={() => onActiveChange(p.index)} onMouseLeave={() => onActiveChange(null)}>
            <span className="vb-csm-dot" data-selected={p.selected || undefined} data-kind={p.kind} aria-hidden="true">{p.glyph}</span>
            <span><b>{p.caption}</b> {p.name}{p.kind === "comp" && p.selected ? " (included)" : ""}
              {" "}<span className="vb-map-coord-src" data-testid="pin-source">· {src}</span></span>
          </li>
        );
      })}
      {[...unplaced, ...missed.map((p): UnplacedRow => ({ number: p.number, name: p.name, caption: p.caption, message: NOT_MAPPED_MESSAGE }))].map((u) => (
        <li key={`u${u.caption}${u.name}`} data-testid="pin-not-mapped">
          <span className="vb-csm-dot" aria-hidden="true">{u.number || "–"}</span>
          <span><b>{u.caption}</b> {u.name} — {u.message}{u.reason ? ` (${u.reason})` : ""}</span>
        </li>
      ))}
    </ol>
  );
}

export function CompSearchMap({
  points,
  unplaced = [],
  subjectQuery,
  radiusMi,
  appliedRadiusMi,
  activeIndex,
  onActiveChange,
  onPinClick,
  loader = loadGoogleMaps,
}: {
  points: MapPoint[];
  unplaced?: UnplacedRow[];
  /** The subject's address, geocoded to centre the circle. */
  subjectQuery: string | null;
  /** The radius the criteria currently ask for (the circle follows it as the agent edits). */
  radiusMi: number;
  /** The radius of the last search actually run; when it differs the caption says so. */
  appliedRadiusMi?: number | null;
  activeIndex: number | null;
  onActiveChange: (index: number | null) => void;
  onPinClick?: (index: number) => void;
  /** Test seam: resolves the Google namespace. */
  loader?: (key: string) => Promise<GoogleNs>;
}) {
  const [mode, setMode] = useState<Mode>("checking");
  const [key, setKey] = useState<string | null>(null);
  const [attribution, setAttribution] = useState("Map data © Google");
  const hostRef = useRef<HTMLDivElement>(null);
  const live = useRef<{
    g: GoogleNs; map: GoogleNs; circle: GoogleNs | null; subject: GoogleNs | null;
    markers: Map<number, GoogleNs>; pos: Map<number, LatLng>; src: Map<number, string>; geo: Map<string, GoogleNs>;
  } | null>(null);
  const pointsRef = useRef(points);
  pointsRef.current = points;
  const cb = useRef({ onActiveChange, onPinClick });
  cb.current = { onActiveChange, onPinClick };

  // 1. Is there a map to draw? One status call; a key is fetched only when it exists.
  useEffect(() => {
    let cancelled = false;
    Promise.resolve()
      .then(() => getMapStatus())
      .then((s) => {
        if (cancelled) return;
        if (!s?.enabled || !s?.browser_key_configured) return setMode("nokey");
        if (s.attribution) setAttribution(s.attribution);
        return getMapBrowserKey().then((k) => {
          if (cancelled) return;
          setKey(k.key);
          setMode("ready");
        });
      })
      .catch(() => { if (!cancelled) setMode("failed"); });
    return () => { cancelled = true; };
  }, []);

  // 2. Create the map once the key is in hand.
  useEffect(() => {
    if (mode !== "ready" || !key || !hostRef.current) return;
    let cancelled = false;
    loader(key)
      .then((g) => {
        if (cancelled || !hostRef.current) return;
        const map = new g.maps.Map(hostRef.current, {
          mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
          gestureHandling: "cooperative", keyboardShortcuts: false,
          center: { lat: 32.78, lng: -96.8 }, zoom: 11,
        });
        live.current = { g, map, circle: null, subject: null, markers: new Map(), pos: new Map(), src: new Map(), geo: new Map() };
        setReadyTick((t) => t + 1);
      })
      .catch(() => { if (!cancelled) setMode("failed"); });
    return () => { cancelled = true; };
  }, [mode, key, loader]);

  const [readyTick, setReadyTick] = useState(0);
  /** Bumped when a comp pin is placed or removed, so the frame follows the set of pins. */
  const [placedTick, setPlacedTick] = useState(0);
  const [subjectPos, setSubjectPos] = useState<{ lat: number; lng: number } | null>(null);
  /** Pins the browser geocoder placed (not a stored point): their source line reads "Google geocode". */
  const [geoPlaced, setGeoPlaced] = useState<number[]>([]);
  /** Pins whose address the browser geocoder could not place under the house-number/ZIP gate. */
  const [geoMissed, setGeoMissed] = useState<number[]>([]);

  const geocode = (q: string): Promise<{ lat: number; lng: number } | null> => {
    const L = live.current;
    if (!L) return Promise.resolve(null);
    const hit = L.geo.get(q);
    if (hit) return Promise.resolve(hit);
    return new Promise((resolve) => {
      new L.g.maps.Geocoder().geocode({ address: q }, (res: GoogleNs, status: string) => {
        // B2: the same house-number/ZIP gate the review map applies (`geocodeMatchPasses`).
        if (status !== "OK" || !res?.[0] || !geocodeMatchPasses(q, res[0])) return resolve(null);
        const loc = res[0].geometry.location;
        const pos = { lat: loc.lat(), lng: loc.lng() };
        L.geo.set(q, pos);
        resolve(pos);
      });
    });
  };

  // 3. Subject pin + radius circle. The circle tracks `radiusMi` live.
  useEffect(() => {
    const L = live.current;
    if (!L || !readyTick) return;
    let cancelled = false;
    const place = (pos: { lat: number; lng: number }) => {
      if (cancelled) return;
      setSubjectPos(pos);
      if (!L.subject) {
        L.subject = new L.g.maps.Marker({
          map: L.map, position: pos, title: "Your home", zIndex: 1000,
          label: { text: "H", color: colors.bgSurface, fontSize: "12px", fontWeight: "700" },
          icon: { path: L.g.maps.SymbolPath.CIRCLE, scale: 13, fillColor: colors.amber, fillOpacity: 1,
                  strokeColor: colors.bgSurface, strokeWeight: 2 },
        });
      }
    };
    if (subjectQuery) void geocode(subjectQuery).then((p) => p && place(p));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyTick, subjectQuery]);

  useEffect(() => {
    const L = live.current;
    if (!L || !subjectPos) return;
    if (!L.circle) {
      L.circle = new L.g.maps.Circle({
        map: L.map, center: subjectPos, radius: radiusMi * METERS_PER_MILE,
        strokeColor: colors.accent, strokeOpacity: 0.7, strokeWeight: 2,
        fillColor: colors.accent, fillOpacity: 0.06, clickable: false,
      });
    } else {
      L.circle.setCenter(subjectPos);
      L.circle.setRadius(radiusMi * METERS_PER_MILE);
    }
  }, [subjectPos, radiusMi, readyTick]);

  // 4. Comp pins: created / removed to match `points`; restyled on selection or activity.
  useEffect(() => {
    const L = live.current;
    if (!L || !readyTick) return;
    let cancelled = false;
    // A pin is kept only while its index still names the same place. After a "Search again" the
    // comps array is replaced, so index 0 can be a different sale: its old pin must go, not be
    // renumbered where the previous sale was.
    const where = (p: MapPoint) => `${p.lat},${p.lon},${p.query ?? ""}`;
    const want = new Map(points.map((p) => [p.index, where(p)]));
    for (const [idx, m] of L.markers) {
      if (want.get(idx) !== L.src.get(idx)) {
        m.setMap(null); L.markers.delete(idx); L.pos.delete(idx); L.src.delete(idx); setPlacedTick((t) => t + 1);
      }
    }
    for (const p of points) {
      const existing = L.markers.get(p.index);
      if (existing) continue;
      const make = (pos: { lat: number; lng: number }) => {
        if (cancelled || L.markers.has(p.index)) return;
        const m = new L.g.maps.Marker({ map: L.map, position: pos, title: `${p.caption}. ${p.name}` });
        m.addListener("mouseover", () => cb.current.onActiveChange(p.index));
        m.addListener("mouseout", () => cb.current.onActiveChange(null));
        m.addListener("click", () => { cb.current.onActiveChange(p.index); cb.current.onPinClick?.(p.index); });
        L.markers.set(p.index, m);
        L.pos.set(p.index, pos);
        L.src.set(p.index, where(p));
        setPlacedTick((t) => t + 1);
        restyle();
      };
      if (p.lat !== null && p.lon !== null) make({ lat: p.lat, lng: p.lon });
      else if (p.query) {
        void geocode(p.query).then((pos) => {
          if (!pos) { setGeoMissed((prev) => (prev.includes(p.index) ? prev : [...prev, p.index])); return; }
          setGeoPlaced((prev) => (prev.includes(p.index) ? prev : [...prev, p.index]));
          make(pos);
        });
      }
    }
    restyle();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, readyTick]);

  function restyle() {
    const L = live.current;
    if (!L) return;
    for (const [idx, m] of L.markers) {
      const p = pointsRef.current.find((x) => x.index === idx);
      if (!p) continue;
      const st = pinStyle(p, idx === activeRef.current);
      m.setIcon({ path: L.g.maps.SymbolPath.CIRCLE, scale: st.scale, fillColor: st.fillColor,
                  fillOpacity: st.fillOpacity, strokeColor: st.strokeColor, strokeWeight: st.strokeWeight });
      m.setLabel({ text: p.glyph, color: st.labelColor, fontSize: "12px", fontWeight: "700" });
      m.setTitle(`${p.caption}. ${p.name}`);   // the title follows the number, like the label does
      m.setZIndex(idx === activeRef.current ? 900 : p.selected ? 500 : 100);
    }
  }
  // 5. Frame the circle and every placed pin. Runs on load and whenever the circle or the set of
  //    placed pins changes; a hover or a tick changes neither, so the agent's own pan and zoom stay.
  useEffect(() => {
    const L = live.current;
    if (!L || !readyTick) return;
    const b = frameBounds({ subject: subjectPos, radiusMi, pins: [...L.pos.values()] });
    if (!b) return;
    L.map.fitBounds(b, MAP_FIT_PADDING_PX);
    // `fitBounds` would zoom to the street for a tight cluster; hold it at a neighborhood.
    L.g.maps.event?.addListenerOnce?.(L.map, "idle", () => {
      const z = L.map.getZoom?.();
      if (typeof z === "number" && z > MAP_MAX_ZOOM) L.map.setZoom(MAP_MAX_ZOOM);
    });
  }, [subjectPos, radiusMi, placedTick, readyTick]);

  // 6. The square. Written through `setProperty` (I-8); re-written when the viewport crosses 768 px.
  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const write = () => {
      for (const [k, v] of Object.entries(mapCanvasStyle(window.innerWidth))) el.style.setProperty(k, v);
    };
    write();
    window.addEventListener("resize", write);
    return () => window.removeEventListener("resize", write);
  }, [mode]);

  const activeRef = useRef<number | null>(activeIndex);
  activeRef.current = activeIndex;
  useEffect(() => { restyle(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeIndex, points]);

  const delta = appliedRadiusMi != null && Math.abs(appliedRadiusMi - radiusMi) > 1e-9;

  if (mode === "nokey" || mode === "failed") {
    return (
      <div className="vb-csm" data-mode={mode}>
        <p className="vb-csm-note">{mode === "nokey" ? NO_KEY_SENTENCE : LOAD_FAILED_SENTENCE}</p>
        <PinKey mapShown={false} points={points} unplaced={unplaced} geoPlaced={geoPlaced} geoMissed={geoMissed}
                activeIndex={activeIndex} onActiveChange={onActiveChange} />
      </div>
    );
  }

  return (
    <div className="vb-csm" data-mode={mode}>
      <div ref={hostRef} className="vb-csm-canvas" role="region" aria-label="Map of your home and the numbered sales"
           data-testid="comp-search-map-canvas" />
      <p className="vb-csm-caption" role="status">
        {delta
          ? `The circle shows ${Number(radiusMi.toFixed(2))} mi. Your last search used ${Number((appliedRadiusMi as number).toFixed(2))} mi; search again to apply it.`
          : `The circle is the ${Number(radiusMi.toFixed(2))} mi search radius around your home.`}
        {" "}Each numbered pin is the sale with the same number below; A and P are homes on the market now (Active or Pending).
      </p>
      <PinKey mapShown points={points} unplaced={unplaced} geoPlaced={geoPlaced} geoMissed={geoMissed}
              activeIndex={activeIndex} onActiveChange={onActiveChange} />
      <p className="vb-csm-attrib">{attribution}. Pin positions are for orientation only; they do not affect the valuation.</p>
    </div>
  );
}
